import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  ToggleButtonGroup, ToggleButton, Stack, Alert, Typography, Box,
} from "@mui/material";
import { recordEorApproval } from "../../utils/documentLifecycleApi";

// CR-2026-SFM-EOR-STAMP — capture the Engineer-of-Record §1.7.0 approval stamp.
export default function EorStampDialog({ doc, open, onClose, onSuccess }) {
  const [decision, setDecision] = useState("approved");
  const [eorName, setEorName] = useState("");
  const [eorLicense, setEorLicense] = useState("");
  const [stampDate, setStampDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => { setDecision("approved"); setEorName(""); setEorLicense(""); setStampDate(""); setNote(""); setError(null); };
  const handleClose = () => { if (!busy) { reset(); onClose(); } };

  const submit = async () => {
    setError(null);
    if (!eorName.trim() || !eorLicense.trim()) {
      setError("EOR name and PE license # are required — the stamp identifies the Engineer of Record.");
      return;
    }
    setBusy(true);
    try {
      const res = await recordEorApproval(doc.id, {
        decision, eor_name: eorName.trim(), eor_license: eorLicense.trim(),
        stamp_date: stampDate || null, note: note || null,
      });
      setBusy(false); reset(); onSuccess(res);
    } catch (e) {
      setBusy(false);
      setError((e && e.response && e.response.data && e.response.data.message) || e.message || "Failed to record EOR stamp.");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record EOR Approval Stamp — AISC 303 §1.7.0</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            The legal proof of §1.7.0 approval is the Engineer-of-Record’s ink stamp on the structural drawing image.
            Recording it here pins this drawing’s image checksum as the proof and unlocks Release for Fabrication.
          </Alert>
          {doc && (
            <Typography variant="body2" color="text.secondary">
              Drawing: <b>{doc.original_name || doc.stored_name || ("Doc #" + doc.id)}</b>
              {doc.pmk ? (" \u00b7 PMK " + doc.pmk) : ""}{doc.doc_type ? (" \u00b7 " + doc.doc_type) : ""}
            </Typography>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">EOR decision (read from the stamp)</Typography>
            <ToggleButtonGroup exclusive value={decision} onChange={(e, v) => v && setDecision(v)} size="small" sx={{ mt: 0.5, display: "block" }}>
              <ToggleButton value="approved">Approved</ToggleButton>
              <ToggleButton value="approved_as_noted">Approved as Noted</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <TextField label="Engineer of Record (name)" value={eorName} onChange={(e) => setEorName(e.target.value)} required fullWidth size="small" />
          <TextField label="EOR PE license #" value={eorLicense} onChange={(e) => setEorLicense(e.target.value)} required fullWidth size="small" />
          <TextField label="Stamp date" type="date" value={stampDate} onChange={(e) => setStampDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth size="small" />
          <TextField label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} fullWidth size="small" multiline minRows={2} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} variant="contained" color="success" disabled={busy}>{busy ? "Recording\u2026" : "Record EOR Stamp"}</Button>
      </DialogActions>
    </Dialog>
  );
}
